import { Component, DestroyRef, OnDestroy, OnInit, Renderer2, Signal, TemplateRef, effect, inject } from '@angular/core'
import { NavigationEnd, NavigationStart, Router, RouterModule } from '@angular/router'
import { Observable, combineLatest, filter, map, tap } from 'rxjs'
import { SignalRService } from '../core/services/signalr-service'
import { ToastrService } from 'ngx-toastr'
import { StaffService } from '../core/services/staff/staff.service'
import { LoadingSpinnerComponent } from '../shared/components/loading-spinner/loading-spinner.component'
import { NgbModal, NgbModule, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap'
import { HeaderComponent } from './secure-components/header/header.component'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AppStore } from '../core/app.store'
import { CompanySettingService } from '../core/services/company-setting.service'
import { Me } from '../shared/interfaces/me/me.interface'
import {
    NgxPermissionsConfigurationService,
    NgxPermissionsModule,
    NgxPermissionsService,
    NgxRolesService,
} from 'ngx-permissions'
import { patchState } from '@ngrx/signals'
import { ButtonComponent } from '../shared/components/form-elements/button/button.component'
import { ReleaseNotesService } from '../core/services/release-notes/release-notes.service'
import { EntityType } from '../shared/signalr/change-notification.interface'
import { PostUpdateEvent } from '../shared/signalr/post-update-event.interface'
import { Notice } from '../shared/interfaces/notices/notice.interface'
import { SidebarComponent } from '../shared/components/sidebar/sidebar/sidebar.component'
import { SidebarHeaderComponent } from '../shared/components/sidebar/sidebar-header/sidebar-header.component'
import { SidebarNavComponent } from '../shared/components/sidebar/sidebar-nav/sidebar-nav.component'
import { SidebarFooterComponent } from '../shared/components/sidebar/sidebar-footer/sidebar-footer.component'
import { secureNavItems } from './secure-nav'
import { NavItem } from '../shared/components/sidebar/nav-item.interface'
import { SidebarTrialComponent } from '../shared/components/sidebar/sidebar-trial/sidebar-trial.component'
import { AuthService } from '@auth0/auth0-angular'
import { AsyncPipe } from '@angular/common'
import { LoadingRequestCounterService } from '../core/interceptors/loading-request-counter.service'
import { I } from '@fullcalendar/resource/internal-common'

@Component({
    selector: 'sf-secure',
    templateUrl: './secure.component.html',
    styleUrls: ['./secure.component.scss'],
    imports: [
        ButtonComponent,
        RouterModule,
        NgbModule,
        NgxPermissionsModule,
        FontAwesomeModule,
        HeaderComponent,
        LoadingSpinnerComponent,
        SidebarComponent,
        SidebarHeaderComponent,
        SidebarNavComponent,
        SidebarFooterComponent,
        SidebarTrialComponent,
        AsyncPipe,
    ],
})
export class SecureComponent implements OnInit, OnDestroy {
    public counter = inject(LoadingRequestCounterService)

    public appStore = inject(AppStore)
    public authService = inject(AuthService)
    private companySettingsService = inject(CompanySettingService)
    private destroyRef = inject(DestroyRef)
    private modalService = inject(NgbModal)
    private offcanvas = inject(NgbOffcanvas)
    private router = inject(Router)
    private signalRService = inject(SignalRService)
    private toastr = inject(ToastrService)
    private staffService = inject(StaffService)
    private permissionsConfigurationService = inject(NgxPermissionsConfigurationService)
    private permissionService = inject(NgxPermissionsService)
    private roleService = inject(NgxRolesService)
    private renderer2 = inject(Renderer2)
    private releaseNotesService = inject(ReleaseNotesService)

    private allNavItems: NavItem[] = [...secureNavItems]
    public filteredNavItems: NavItem[] = [...secureNavItems]

    title = ''

    onlineUsersViewingCurrentPage: number[] = []

    isLoading!: Observable<boolean>
    apiLoaded!: Observable<boolean>

    me: Signal<Me | null> = this.appStore.me

    constructor() {
        effect(() => {
            this.appStore.postCounter()
            this.updateNavCounts()
        })
    }

    userLoadedEffect = effect(() => {
        const roles = this.appStore.roles()
        const permissions = this.appStore.permissions()
        console.log('building nav with roles', roles, 'and permissions', permissions)
        this.buildNavWithPermissions(roles, permissions)
    })

    async ngOnInit(): Promise<void> {
        this.appStore.loadMe()
        this.appStore.loadCompanySettings()

        this.permissionsConfigurationService.addPermissionStrategy(
            'disable',
            (templateRef: TemplateRef<any> | undefined) => {
                // if (templateRef?.elementRef) {
                //     console.log('Disabling', templateRef.elementRef.nativeElement.nextSibling)
                //     this.renderer2.addClass(templateRef.elementRef.nativeElement.nextSibling, 'disabled')
                // }
                // debugger
                // alert('disable' + templateRef?.elementRef.nativeElement.nextSibling.name)
                debugger
                console.log('Disabling', templateRef?.elementRef.nativeElement.nextSibling)
                this.renderer2.addClass(templateRef, 'disabled')
                this.renderer2.addClass(templateRef?.elementRef.nativeElement.nextSibling, 'disabled')
                if (templateRef?.elementRef.nativeElement.previousSibling) {
                    this.renderer2.addClass(templateRef!.elementRef.nativeElement.previousSibling, 'disabled')
                }

                //this.renderer2.addClass(templateRef?.elementRef.nativeElement.nextSibling, 'disabled')
                // console.log(templateRef?.elementRef.nativeElement.nextSibling)
                // this.renderer2.setAttribute(templateRef?.elementRef.nativeElement.nextSibling, 'disabled', 'true')
                //or directly not recommended
                //templateRef?.elementRef.nativeElement.nextSibling.setAttribute('disabled', true)
            }
        )

        // this.permissionsConfigurationService.setDefaultOnUnauthorizedStrategy('disable')

        this.permissionsConfigurationService.addPermissionStrategy('enable', (a: TemplateRef<any> | undefined) => {
            if (a?.elementRef.nativeElement.previousSibling) {
                this.renderer2.removeClass(a!.elementRef.nativeElement.previousSibling, 'disabled')
            }
        })

        await this.signalRService.initiateSignalrConnection()

        // this.signalRService.appointmentChanges$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((change) => {
        //     console.log('Appointment change', change)
        // })

        this.signalRService
            .getChangesFor<PostUpdateEvent>(EntityType.Post)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((change) => {
                const { postsToProcessCount, urgentPostsToProcessCount } = change.data.postCounter

                patchState(this.appStore, {
                    postCounter: {
                        postsToProcessCount: postsToProcessCount,
                        urgentPostsToProcessCount: urgentPostsToProcessCount,
                    },
                })
            })

        this.signalRService
            .getChangesFor<Notice>(EntityType.Notice)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((change) => {
                const currentCount = this.appStore.unreadNoticesCount()
                this.appStore.setUnreadNoticesCount(currentCount + 1)
                this.showNoticeToastNotification()
            })

        this.signalRService.connection.on('newConnection', (response: any) => {
            console.log(response.ConnectionId + ' Joined')
        })

        this.signalRService.connection.on('receiveNotification', (message: string) => {
            alert(message)
        })

        this.signalRService.connection.on('ReceiveChangeNotification', (message: any) => {
            console.log(message)
        })

        // update nav when permissions or roles change
        // combineLatest([this.permissionService.permissions$, this.roleService.roles$])
        //     .pipe(takeUntilDestroyed(this.destroyRef))
        //     .subscribe(async () => {
        //         const roles = await this.roleService.getRoles()
        //         const permissions = await this.permissionService.getPermissions()
        //         console.log('Roles changed', roles)
        //         console.log('Permissions changed', permissions)
        //          this.buildNavWithPermissions()
        //         this.updateNavCounts()
        //     })

        // this.signalRService.connection.on('ReceiveNotification', (message: string) => {
        //     this.toastr.info(message, 'Notification')
        // })

        // this.signalRService.connection.on('ReceiveMessage', (title: string, message: string, type: string = 'info') => {
        //     switch (type) {
        //         case 'success':
        //             this.toastr.success(message, title)
        //             break
        //         case 'warning':
        //             this.toastr.warning(message, title)
        //             break
        //         case 'danger':
        //             this.toastr.error(message, title)
        //             break
        //         default:
        //             this.toastr.info(message, title)
        //             break
        //     }
        // })

        this.signalRService.connection.on('UserJoinedPage', (user: string, onlineUsersViewingCurrentPage: number[]) => {
            // debugger
            // console.log(
            //     `${user} ${onlineUsersViewingCurrentPage} has joined the group. Online users include: ${onlineUsersViewingCurrentPage}`
            // )
            this.onlineUsersViewingCurrentPage = [...onlineUsersViewingCurrentPage]
        })

        this.signalRService.connection.on('UserLeftPage', (user: string, onlineUsersViewingCurrentPage: number[]) => {
            // console.log(
            //     `${user} ${onlineUsersViewingCurrentPage} has left the page. Online users include: ${onlineUsersViewingCurrentPage}`
            // )
            this.onlineUsersViewingCurrentPage = [...onlineUsersViewingCurrentPage]
        })

        this.setTitle()

        this.router.events
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd),
                tap((event) => {
                    console.log('Send removal', event)
                    if (event instanceof NavigationStart) {
                        this.onlineUsersViewingCurrentPage = []
                        const previousNavigation = this.router.getCurrentNavigation()
                        const previousUrl = previousNavigation?.previousNavigation?.finalUrl?.toString()
                        if (previousUrl) {
                            //this.signalRService.connection.invoke('LeavePageGroup', previousUrl)
                        }
                    }
                }),
                filter((event) => event instanceof NavigationEnd),
                tap((event) => {
                    console.log('Send new page', event)
                    if (event instanceof NavigationEnd) {
                        this.modalService.dismissAll()
                        this.offcanvas.dismiss()
                        //this.signalRService.connection.invoke('JoinPageGroup', event.url)
                    }
                }),
                map(() => this.getTitleFromRoute())
            )
            .subscribe((title: string) => {
                this.setTitle()
            })

        this.staffService.getAllStaff().subscribe((staff) => this.appStore.setStaff(staff))

        if (this.me() != null) {
            const { acknowledgedLatestReleaseNote, unreadNoticesCount } = this.me()!

            if (!acknowledgedLatestReleaseNote) {
                this.releaseNotesService.checkForLatestReleaseDialog()
            }

            if (unreadNoticesCount > 0) {
                this.showNoticeToastNotification()
            }
        }
    }

    buildNavWithPermissions(userRoles: string[], userPermissions: string[]): void {
        this.filteredNavItems = [
            ...this.allNavItems
                .map((item) => this.processItem(item, userRoles, userPermissions))
                .filter((i): i is NavItem => i !== null),
        ]
    }

    private processItem(item: NavItem, userRoles: string[], userPermissions: string[]): NavItem | null {
        let doesNotHaveRole = false

        if (item.roles) {
            const hasRole = Array.isArray(item.roles)
                ? item.roles.some((role) => userRoles.includes(role))
                : userRoles.includes(item.roles)
            doesNotHaveRole = !hasRole

            console.log(`Item: ${item.name} requires roles ${item.roles}, User has role: ${hasRole}`)

            if (doesNotHaveRole) {
                return null // 🚨 completely remove this item
            }
        }

        let doesNotHavePermission = false

        if (item.permissions) {
            const hasPermission = Array.isArray(item.permissions)
                ? item.permissions.some((permission) => userPermissions.includes(permission))
                : userPermissions.includes(item.permissions)

            doesNotHavePermission = !hasPermission

            if (item.permissionStrategy === 'remove' && doesNotHavePermission) {
                return null // 🚨 completely remove this item
            }

            if (item.permissionStrategy === 'disable') {
                item.attributes = {
                    ...(item.attributes || {}),
                    disabled: doesNotHavePermission,
                }
            }
        }

        // recurse into children
        if (item.children && item.children.length > 0) {
            const processedChildren = item.children.map((child) => this.processItem(child, userRoles, userPermissions))

            // filter out removed children
            item.children = processedChildren.filter((c) => c !== null) as NavItem[]

            // If all children are removed and this item has no url, optionally remove parent too
            if (!item.url && item.children.length === 0) {
                return null
            }
        }

        return { ...item }
    }

    updateNavCounts(): void {
        const { postsToProcessCount, urgentPostsToProcessCount } = this.appStore.postCounter()
        const totalPostCount = postsToProcessCount + urgentPostsToProcessCount
        this.filteredNavItems = this.filteredNavItems.map((item) => {
            if (item.counterKey === 'posts') {
                item.badge = {
                    color: totalPostCount > 0 ? 'danger' : 'success',
                    text: `${totalPostCount}` || '0',
                }
            }

            return { ...item }
        })
    }

    showNoticeToastNotification() {
        this.toastr
            .info('Click here to be redirected to the notices page for more information.', 'New Notice Available', {
                timeOut: 0,
            })
            .onTap.subscribe(() => this.router.navigateByUrl('notices'))
    }

    toggleMenu(): void {
        this.appStore.toggleSidebarVisibility()
    }

    private setTitle(): void {
        this.title = this.getTitleFromRoute()
    }

    private getTitleFromRoute(): string {
        let route = this.router.routerState.root
        let routeTitle = ''
        while (route!.firstChild) {
            route = route!.firstChild
        }
        if (route.snapshot.title) {
            routeTitle = route.snapshot.title
        }
        return routeTitle
    }

    logout(): void {
        this.signalRService.endConnection()
        this.authService.logout()
    }

    ngOnDestroy(): void {
        this.signalRService.endConnection()
    }
}
